import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  Filter,
  Clock,
  CalendarDays,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const PUBLIC_ORIGIN = import.meta.env.VITE_PUBLIC_ORIGIN || 'https://hitzlink.com';

interface ArticleRow {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  category: string | null;
  tags: string[] | null;
  status: 'draft' | 'published' | 'archived';
  published_at: string | null;
  scheduled_at: string | null;
  noindex: boolean;
  view_count: number;
  reading_time_minutes: number | null;
  word_count: number | null;
  created_at: string;
  updated_at: string;
  author_id: string | null;
  author_name_override: string | null;
}

const STATUS_FILTERS: { id: ArticleRow['status'] | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'published', label: 'Published' },
  { id: 'draft', label: 'Drafts' },
  { id: 'archived', label: 'Archived' },
];

const STATUS_PILL: Record<ArticleRow['status'], string> = {
  published: 'bg-green-100 text-green-700 border-green-200',
  draft: 'bg-amber-100 text-amber-700 border-amber-200',
  archived: 'bg-gray-100 text-gray-600 border-gray-200',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

export const ArticleList: React.FC = () => {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const [items, setItems] = useState<ArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ArticleRow['status'] | 'all'>('all');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const authHeader = useMemo<Record<string, string>>(
    () => (token ? { Authorization: `Bearer ${token}` } : ({} as Record<string, string>)),
    [token]
  );

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (status !== 'all') params.set('status', status);
      if (q.trim()) params.set('q', q.trim());
      params.set('limit', '100');

      const res = await fetch(`${API_URL}/admin/articles?${params.toString()}`, {
        headers: authHeader,
      });
      if (!res.ok) throw new Error('Failed to load articles');
      const json = await res.json();
      setItems(json.items || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchList();
    // Re-fetch whenever the status filter changes. Search is debounced
    // separately by listening to `q` with a 300ms delay so we don't
    // hammer the API on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, token]);

  useEffect(() => {
    if (!token) return undefined;
    const handle = setTimeout(() => fetchList(), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const togglePublish = async (row: ArticleRow) => {
    setBusyId(row.id);
    try {
      const action = row.status === 'published' ? 'unpublish' : 'publish';
      const res = await fetch(`${API_URL}/admin/articles/${row.id}/${action}`, {
        method: 'POST',
        headers: authHeader,
      });
      if (!res.ok) throw new Error();
      toast.success(action === 'publish' ? 'Published' : 'Moved to draft');
      await fetchList();
    } catch {
      toast.error('Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row: ArticleRow) => {
    if (!confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      const res = await fetch(`${API_URL}/admin/articles/${row.id}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      if (!res.ok) throw new Error();
      toast.success('Article deleted');
      setItems((prev) => prev.filter((a) => a.id !== row.id));
    } catch {
      toast.error('Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const createNew = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/articles`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: 'Untitled article',
          content_md: '# Untitled article\n\nStart writing here...',
        }),
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      navigate(`/admin/articles/${created.id}/edit`);
    } catch {
      toast.error('Could not create draft');
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-7 h-7 text-blue-600" />
              Articles
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Marketing blog content surfaced at <span className="font-mono text-gray-700">/blog</span> with full SEO + JSON-LD.
            </p>
          </div>
          <button
            onClick={createNew}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            New article
          </button>
        </header>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 p-4 border-b border-gray-200">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search titles..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" role="tablist">
              <Filter className="w-4 h-4 text-gray-500 ml-2" aria-hidden />
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setStatus(f.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    status === f.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                  role="tab"
                  aria-selected={status === f.id}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="p-10 text-center text-gray-500">Loading articles...</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-gray-700 mb-1">No articles yet</p>
              <p className="text-sm">Click "New article" to create your first post.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">Title</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Category</th>
                    <th className="text-left px-4 py-3">Published</th>
                    <th className="text-right px-4 py-3">Views</th>
                    <th className="text-right px-4 py-3 w-[180px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/admin/articles/${row.id}/edit`)}
                          className="text-left"
                        >
                          <div className="font-medium text-gray-900 line-clamp-1">{row.title}</div>
                          <div className="text-xs text-gray-500 font-mono mt-0.5">/blog/{row.slug}</div>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_PILL[row.status]}`}>
                          {row.status}
                          {row.noindex && <span title="noindex" className="ml-1">·noidx</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{row.category || '—'}</td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
                          {formatDate(row.published_at)}
                        </span>
                        {row.reading_time_minutes ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500 ml-2">
                            <Clock className="w-3 h-3" />
                            {row.reading_time_minutes}m
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{row.view_count?.toLocaleString() ?? 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {row.status === 'published' && (
                            <a
                              href={`${PUBLIC_ORIGIN}/blog/${row.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                              title="Open public URL"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => togglePublish(row)}
                            disabled={busyId === row.id}
                            className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors disabled:opacity-50"
                            title={row.status === 'published' ? 'Unpublish' : 'Publish'}
                          >
                            {row.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => navigate(`/admin/articles/${row.id}/edit`)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(row)}
                            disabled={busyId === row.id}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ArticleList;
